<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="connectionLicenseForm"
            name="connectionLicenseForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-md-6">
                <div class="form-group">
                    <!--Name-->
                    <base-input
                        id="connectionLicenseName"
                        type="text"
                        :text="`Naam`"
                        required
                        v-model="connectionLicense.name"
                    >
                    </base-input>
                    <br />
                    <!--Url-->
                    <base-input
                        id="connectionLicenseUrl"
                        type="text"
                        :text="`Url`"
                        required
                        v-model="connectionLicense.url"
                    >
                    </base-input>
                    <br />
                    <!--Type-->
                    <base-select
                        :name="`connectionLicenseType`"
                        :text="$t('columnTypeTitle')"
                        :options="connectionTypeOptions"
                        required
                        :value="connectionLicense.type"
                        @selected="$_onTypeSelected"
                    >
                    </base-select>
                    <div
                        v-if="mode === modeAddConst"
                        class="form-group col-xs-8 no-margin-bottom"
                    >
                        <x-checkbox
                            :name="'standardLicenced'"
                            v-model="connectionLicense.standardLicenced"
                            :text="$t('labelStandardLicensed')"
                        >
                        </x-checkbox>
                        <x-checkbox
                            :name="'standardAvailable'"
                            :disabled="!connectionLicense.standardLicenced"
                            v-model="connectionLicense.standardAvailable"
                            :text="$t('labelStandardAvailable')"
                        >
                        </x-checkbox>
                    </div>
                    <!--Image-->
                    <label>{{ $t('icon') }}</label>
                    <image-loader
                        name="connectionLicenseImageData"
                        :value="connectionLicense.imageData"
                        @input="$_onSelectImage"
                    >
                    </image-loader>
                </div>
                <!-- Save/cancel/remove buttons -->
                <SubmitCancelRemove
                    :is-saving="isSaving"
                    :is-deleting="isDeleting"
                    :mode="mode"
                    :isBtnSaveDisabled="isBtnSaveDisabled"
                    @cancel="$_onCancel"
                    @remove="$_onRemove"
                >
                </SubmitCancelRemove>
            </div>
        </b-form>
    </div>
</template>

<script>
    /* Store imports */
    import { mapState } from 'vuex';
    import { MODE_ADD, MODE_EDIT } from '../../constants';
    import BaseSelect from '../../components/BaseComponents/base-select.vue';
    import BaseInput from '../../components/BaseComponents/base-input.vue';
    import imageLoader from '../../components/ImageLoader.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import xcheckbox from '../../components/BaseComponents/x-checkbox.vue';

    export default {
        name: 'UrlConnectionLicensesDetail',
        mixins: [modalMixin],

        components: {
            SubmitCancelRemove,
            'base-input': BaseInput,
            'image-loader': imageLoader,
            'base-select': BaseSelect,
            'x-checkbox': xcheckbox
        },

        props: {
            // url connection license id
            id: { type: Number, default: -1 }
        },

        data: function () {
            return {
                // Constants
                modeAddConst: MODE_ADD,

                mode: 0,
                title: '',
                connectionLicense: {
                    name: '',
                    imageData: '',
                    type: 0,
                    url: '',
                    standardLicenced: false,
                    standardAvailable: false
                },
                isSaving: false,
                isDeleting: false,
                connectionTypeOptions: [
                    { text: 'Beeldmateriaal ', value: 1 },
                    { text: 'Overig', value: 2 }
                ]
            };
        },

        computed: {
            ...mapState(['userInfo']),
            /**
             * Check if connection license can be saved
             * @returns {boolean} indicating if organization can be saved
             */
            isBtnSaveDisabled: function () {
                return this.isSaving || this.isDeleting;
            }
        },

        created() {
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('urlConnectionLicenseDetailEditTitle');
                this.$_getConnectionLicense();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('urlConnectionLicenseDetailAddTitle');
            }
        },

        methods: {
            async $_getConnectionLicense() {
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetUrlConnectionLicenseById',
                        parameters: {
                            id: this.id,
                        }
                    });
                    this.connectionLicense = Object.assign({}, result?.data);
                    this.connectionLicense.imageData = Buffer.from(
                        this.connectionLicense.imageData,
                        'base64'
                    ).toString();
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: 'Ophalen van connectie licentie mislukt',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            $_goToOverview() {
                this.$router.push({ path: '/dashboard/connectionLicenses' });
            },

            $_onCancel: function () {
                this.$_goToOverview();
            },

            /*
             * Removes the connection license
             */
            $_onRemove() {
                if (!this.userInfo.isSuper) {
                    this.msgBoxOk({
                        message: this.$t('noDeletePermittedNoSuperUser')
                    });
                    return;
                }

                const confirmMessage = {
                    message: this.$t('urlConnectionLicenseDeleteConfirmation')
                };

                this.msgBoxConfirm(confirmMessage).then(async (confirm) => {
                    if (confirm) {
                        this.isDeleting = true;
                        try {
                            await this.$store.dispatch('useApiData', {
                                name: 'DeleteUrlConnectionLicense',
                                parameters: {
                                    id: this.id,
                                }
                            });
                            this.isDeleting = false;
                            const data = {
                                message: 'Koppeling licentie verwijderd',
                                variant: 'success',
                                autoHideDelay: 5000
                            };
                            this.showToast(data);
                            this.$_goToOverview();
                        } catch (error) {
                            const data = {
                                message: error.message,
                                variant: 'danger',
                                title: this.$t('saveFailureTitle'),
                                autoHideDelay: 5000
                            };
                            this.showToast(data);
                            this.isDeleting = false;
                        }
                    }
                });
            },

            $_onTypeSelected(input) {
                this.connectionLicense.type = input;
            },

            $_convertData() {
                // Convert image in case of string representaion
                if (this.connectionLicense.imageData.startsWith('data:')) {
                    this.connectionLicense.imageData = Buffer.from(
                        this.connectionLicense.imageData
                    ).toString('base64');
                }
            },

            /**
             * Converts the selected image data to string in base-64
             * @param value
             */
            $_onSelectImage(value) {
                this.connectionLicense.imageData =
                    Buffer.from(value).toString('base64');
            },

            async $_onSave() {
                this.$_convertData();
                this.isSaving = true;
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'SaveUrlConnectionLicense',
                        data: this.connectionLicense
                    });
                    this.isSaving = false;
                    const data = {
                        message: 'Koppeling licentie opgeslagen',
                        variant: 'success',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                    this.$_goToOverview();
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                    this.isSaving = false;
                }
            },

            $_onSubmit() {
                if (this.$refs.connectionLicenseForm.checkValidity()) {
                    this.$_onSave();
                }
            }
        }
    };
</script>
